import { type BlokProps, StoryblokComponent } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import BlokWrap from "@/components/bloks/BlokWrap";
import { Carousel } from "@/components/ui/Carousel";
import {
	IntroSection,
	IntroSectionDescription,
	IntroSectionTitle,
} from "@/components/ui/IntroSection";
import type { BlokRouter } from "@/data/routes/bloks.route";
import {
	ProductCard,
	ProductCardSkeleton,
} from "@/features/products/ProductCard";
import { Link } from "@/i18n/routing";
import { sbLink } from "@/lib/storyblok/link";
import type { BlokProducts } from "@/storyblok";
import { sizes } from "../Asset";
import styles from "./Products.module.css";

export const ProductsBlok = ({
	blok,
	data,
	insideSection,
	preview,
}: BlokProps<BlokProducts, BlokRouter["products"]> & {
	insideSection?: boolean;
}) => {
	const endContent = blok.endContent?.at(0);
	const viewMore = blok.viewMore?.at(0);
	const viewMoreLink = viewMore?.href
		? sbLink(viewMore?.href)?.href
		: undefined;

	return (
		<BlokWrap editable={blok} as="section" className={styles.root}>
			{!insideSection && (
				<IntroSection>
					{blok.title && <IntroSectionTitle>{blok.title}</IntroSectionTitle>}
					{blok.description && (
						<IntroSectionDescription>
							{blok.description}
						</IntroSectionDescription>
					)}
				</IntroSection>
			)}

			<Carousel
				className={styles.carousel}
				options={{
					hide: (data?.length ?? 0) <= 5,
					type: "chevron",
					slidesToScroll: 1,
					dragFree: true,
				}}
			>
				{data?.map((product, i) => {
					return (
						<ProductCard
							sizes={sizes(75, 25)}
							key={`${product.displayItemId}-${i}`}
							product={product}
						/>
					);
				})}
				{endContent && (
					<StoryblokComponent
						blok={endContent}
						preview={preview}
						className={clsx(
							endContent.component === "media" ? styles.media : undefined,
						)}
					/>
				)}
			</Carousel>

			{!insideSection && viewMore && viewMoreLink && (
				<Link href={viewMoreLink} className={styles.link}>
					{viewMore.text}
				</Link>
			)}
		</BlokWrap>
	);
};

export const ProductsSkeleton = ({
	blok,
	size = 6,
}: ComponentProps<typeof ProductsBlok> & {
	size?: number;
}) => {
	return (
		<BlokWrap editable={blok} as="section" className={styles.root}>
			<Carousel className={styles.carousel}>
				{Array.from({ length: size })
					.fill(null)
					.map((_, i) => (
						<ProductCardSkeleton hideColors key={i} />
					))}
			</Carousel>
		</BlokWrap>
	);
};
